import { Box, useTheme } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import { ChevronRight } from '@material-ui/icons'
import clsx from 'clsx'
import React, { FC, useMemo, useState } from 'react'
import { get, isEqual } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { AuthPersonsSelectType, ControlledTextFieldProps, OptionType } from '../../../types'
import { MobileAutocompleteModal } from '../MobileAutocompleteModal'

const useStyles = (readOnly: boolean | undefined) =>
  makeStyles((theme: Theme) => ({
    chevron: {
      position: 'absolute',
      top: '50%',
      transform: 'translateX(-50%)',
      right: theme.spacing(1.5),
      rotate: '90deg',
      color: '#999',
    },
    input: {
      backgroundColor: readOnly ? 'rgb(245 245 245)' : 'inherit',
      '& .MuiInputBase-root': {
        height: theme.spacing(6.5),
      },
      '& label': {
        position: 'relative',
      },
    },
    whiteBackGround: {
      width: '100%',
      background: '#FFFFFF',
      borderRadius: '4px 4px 0px 0px',
    },
  }))()

const MobileSelectorComponent: FC<
  {
    name: string
    readOnly?: boolean
    /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/
    options: Array<any> | AuthPersonsSelectType[]
    handleChange: (selectedValue: string | null) => void
    doubleRows?: boolean
    isDynamicLabels?: boolean
  } & Partial<ControlledTextFieldProps>
> = ({ name, readOnly, options, handleChange, doubleRows, isDynamicLabels, ...rest }) => {
  const classes = useStyles(readOnly)
  const theme = useTheme()

  const [isOpen, setIsOpen] = useState(false)

  const { errors, watch } = useFormContext()
  const error = get(errors, name || 'FormAutocompleteSelect', null)
  const currentValue = watch(name)

  const mobileOptions = useMemo(() => options.filter((option) => !option.disabled), [options])

  const selectedOption = useMemo(() => options.find(({ key }) => key === currentValue), [
    currentValue,
    options,
  ])

  const handleToggle = () => !readOnly && setIsOpen((prevState) => !prevState)

  const handleSelect = (selectedValue: string | null) => () => {
    handleChange(selectedValue)
    setIsOpen(false)
  }

  return (
    <>
      <Box width="100%" position="relative" onClick={handleToggle}>
        <TextField
          error={!!error}
          helperText={error ? error.message : null}
          disabled={readOnly}
          {...rest}
          inputProps={{
            ...rest.inputProps,
            readOnly: true,
            tabIndex: -1,
            'data-test': `autotest-${name}`,
            style: { textOverflow: 'ellipsis', paddingRight: theme.spacing(4), paddingTop: 0 },
          }}
          InputLabelProps={{
            shrink: !!selectedOption?.label,
            style: { paddingRight: theme.spacing(5) },
          }}
          value={selectedOption?.label}
          className={clsx(classes.input, !readOnly && classes.whiteBackGround)}
          fullWidth
        />
        <ChevronRight className={classes.chevron} />
      </Box>

      <MobileAutocompleteModal
        open={isOpen}
        onClose={handleToggle}
        onSelect={handleSelect}
        textHeader={(rest?.label as string) || name}
        options={mobileOptions as OptionType[]}
        currentValue={selectedOption?.key as string}
        doubleRows={doubleRows}
        isDynamicLabels
      />
    </>
  )
}

export const MobileSelector = React.memo(MobileSelectorComponent, isEqual)
